import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'antd';
import Table, { TableColumn } from '../../../../../components/Table';
import { ITransactionsTable } from '../../types';
import { DiscountRow, ResponsiveColumn, ResponsiveRow, ResponsiveTable, TableContainer } from '../../styles';
import useGetMe from '../../../../../hooks/useCurrentUser';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import PainelDePagamentosService from '../../service/PainelDePagamentosService';
import { getErrorMessage } from '../../../../../utils/getErrorMessage';

const LiveTable: React.FC<ITransactionsTable> = (props: ITransactionsTable) => {
  const user = useGetMe();
  const painelDePagamentosService = useMemo(() => new PainelDePagamentosService(), []);
  const { dataFilter, setPagamentos, setTotals, setBulkFlags } = props;

  const [liveTransactions, setLiveTransactions] = useState<any[]>([]);
  const [dataFilterState, setDataFilterState] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [externalCount, setExternalCount] = useState(0);

  const formatCnpj = (cnpj: any) => {
    if (cnpj === undefined) return;
    const formattedPattern = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
    if (formattedPattern.test(cnpj)) return cnpj;
    cnpj = cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) return cnpj;
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatMoney = (value: number) => {
    return (value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchData = async (page: number, size: number, filter: any) => {
    setIsLoading(true);
    setBulkFlags({});
    try {
      const [pagamentosResponse, pendingSignatures] = await Promise.all([
        painelDePagamentosService.getPagamentos(page - 1, size, filter),
        painelDePagamentosService.getAssinaturasPendentes(),
      ]);

      const items: any[] = (pagamentosResponse.data.items ?? pagamentosResponse.data.records ?? []).map((item: any) => ({
        ...item,
        merchant: item.merchantId ?? item.merchant,
        valorVenda: item.totalGross ?? item.valorVenda,
        valorLiquido: item.totalLiquid ?? item.valorLiquido,
        quantidade: item.count ?? item.quantidade,
        creditoPago: item.creditPaid ?? item.creditoPago,
        debitoPago: item.debitPaid ?? item.debitoPago,
        pixPago: item.pixPaid ?? item.pixPago,
      }));

      const pendingByMerchant = new Map<string, number>(
        pendingSignatures.map((sign) => [sign.merchantId, sign.totalPending])
      );

      // Mesma regra do Flux: o desconto nunca passa do líquido do período, então a coluna
      // mostra o que será efetivamente debitado, não o total que o merchant deve.
      const updatedItems = items.map((payment: any) => {
        const pending = pendingByMerchant.get(payment.merchant) ?? 0;
        const liquido = payment.valorLiquido ?? 0;
        const totalSignValue = liquido > 0 ? Math.min(liquido, pending) : 0;
        return {
          ...payment,
          signValue: payment.signValue ? payment.signValue + totalSignValue : totalSignValue,
        };
      });

      setLiveTransactions(updatedItems);
      setPagamentos(updatedItems);
      setExternalCount(pagamentosResponse.data.totalCount ?? updatedItems.length);
      setTotals({
        totalGross:  pagamentosResponse.data.totalGross  ?? 0,
        totalLiquid: pagamentosResponse.data.totalLiquid ?? 0,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);
      if (errorMessage === 'Request failed with status code 401') {
        window.location.assign(`${window.location.origin}/login`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const _isMounted = useRef(false);
  useEffect(() => {
    if (!_isMounted.current) { _isMounted.current = true; return; }
    if (dataFilter) {
      setCurrentPage(1);
      setDataFilterState(dataFilter);
    }
  }, [dataFilter]);

  useEffect(() => {
    if (dataFilterState) {
      fetchData(currentPage, pageSize, dataFilterState);
    }
  }, [dataFilterState, currentPage, pageSize]);

  const changeDebitoPago = (e: any, record: any) => {
    const updated = [...liveTransactions];
    const index = updated.findIndex((item) => item === record);
    updated[index].debitoPago = e.target.checked;
    setLiveTransactions(updated);
    setPagamentos(updated);
  };

  const changeDebitoPagoTodos = (e: any) => {
    const updated = [...liveTransactions];
    updated.forEach((value) => { value.debitoPago = e.target.checked; });
    setLiveTransactions(updated);
    setPagamentos(updated);
    setBulkFlags((prev: any) => ({ ...prev, debitPaid: e.target.checked }));
  };

  const changePixPago = (e: any, record: any) => {
    const updated = [...liveTransactions];
    const index = updated.findIndex((item) => item === record);
    updated[index].pixPago = e.target.checked;
    setLiveTransactions(updated);
    setPagamentos(updated);
  };

  const changePixPagoTodos = (e: any) => {
    const updated = [...liveTransactions];
    updated.forEach((value) => { value.pixPago = e.target.checked; });
    setLiveTransactions(updated);
    setPagamentos(updated);
    setBulkFlags((prev: any) => ({ ...prev, pixPaid: e.target.checked }));
  };

  const changeCreditoPago = (e: any, record: any) => {
    const updated = [...liveTransactions];
    const index = updated.findIndex((item) => item === record);
    updated[index].creditoPago = e.target.checked;
    setLiveTransactions(updated);
    setPagamentos(updated);
  };

  const changeCreditoPagoTodos = (e: any) => {
    const updated = [...liveTransactions];
    updated.forEach((value) => { value.creditoPago = e.target.checked; });
    setLiveTransactions(updated);
    setPagamentos(updated);
    setBulkFlags((prev: any) => ({ ...prev, creditPaid: e.target.checked }));
  };

  return (
    <div>
      <LoadingOverlay show={isLoading} relative />
      <TableContainer>
        <Table
          dataSource={liveTransactions}
          pagination={{
            defaultPageSize: 20,
            defaultCurrent: 0,
            current: currentPage,
            showSizeChanger: true,
            onShowSizeChange: (current: number, size: number) => {
              setPageSize(size);
              setCurrentPage(current);
            },
            total: externalCount,
            onChange: (pageNum) => {
              setCurrentPage(pageNum);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} pagamentos`,
          }}
        >
          <TableColumn title="Empresa" dataIndex="businessName" key="businessName" />

          {user.userType === 3 && (
            <TableColumn title="Codigo do lojista" dataIndex="merchant" key="merchant" />
          )}

          <TableColumn title="CNPJ" render={formatCnpj} dataIndex="document" key="document" />

          <TableColumn title="Quantidade" dataIndex="quantidade" key="quantidade" />

          <TableColumn render={formatMoney} title="Valor da venda" dataIndex="valorVenda" key="valorVenda" />

          <TableColumn render={formatMoney} title="Valor Líquido" dataIndex="valorLiquido" key="valorLiquido" />

          <TableColumn render={formatMoney} title="Desconto de assinaturas" dataIndex="signValue" key="signValue" />

          <TableColumn
            key="debitoPago"
            title={
              <div>Débito
                <Checkbox onChange={(e) => { changeDebitoPagoTodos(e); }} name="debito" />
              </div>
            }
            render={(text: any, record: any) => (
              <Checkbox checked={record.debitoPago} onChange={(e) => { changeDebitoPago(e, record); }} name="debito" />
            )}
          />

          <TableColumn
            title={
              <div>Crédito <Checkbox name="credito" onChange={(e) => { changeCreditoPagoTodos(e); }} /></div>
            }
            render={(text: any, record: any) => (
              <Checkbox checked={record.creditoPago} onChange={(e) => { changeCreditoPago(e, record); }} name="credito" />
            )}
          />

          <TableColumn
            title={
              <div>Pix <Checkbox name="pix" onChange={(e) => { changePixPagoTodos(e); }} /></div>
            }
            render={(text: any, record: any) => (
              <Checkbox checked={record.pixPago} name="pix" onChange={(e) => { changePixPago(e, record); }} />
            )}
          />
        </Table>
      </TableContainer>
      <ResponsiveTable>
        <ResponsiveRow style={{ borderBottom: '1px solid gray', borderRadius: '0px' }}>
          <ResponsiveColumn><h4>Empresa</h4></ResponsiveColumn>
          <ResponsiveColumn><h4>Qtd</h4></ResponsiveColumn>
          <ResponsiveColumn><h4>Valor da Venda</h4></ResponsiveColumn>
          <ResponsiveColumn><h4>Valor Liquido</h4></ResponsiveColumn>
        </ResponsiveRow>
        {liveTransactions.map((sale, index) => (
          <ResponsiveRow
            key={index}
            style={{ backgroundColor: 'white', boxShadow: '2px 2px 6px rgba(0, 0, 0, 0.2)', cursor: 'pointer' }}
          >
            <ResponsiveColumn style={{ width: '30%' }}>
              <p>{sale.businessName}</p>
            </ResponsiveColumn>
            <ResponsiveColumn>
              <p>{sale.quantidade}</p>
            </ResponsiveColumn>
            <ResponsiveColumn>
              <p>{formatMoney(sale.valorVenda)}</p>
            </ResponsiveColumn>
            <ResponsiveColumn>
              <p>{formatMoney(sale.valorLiquido)}</p>
            </ResponsiveColumn>
          </ResponsiveRow>
        ))}
      </ResponsiveTable>
    </div>
  );
};

export default LiveTable;
